import { ReactNode } from 'react';
import styled from '@emotion/styled';
import Card from './Card';
import Title from './Title';
import Description from './Description';
import IconBadge from './IconBadge';
import CountBadge from './CountBadge';
import Cta from './Cta';
import { uiTheme as t } from './theme';

export interface SectionCardProps {
  /** Icono de la sección. */
  icon: ReactNode;
  iconBg?: string;
  iconColor?: string;
  title: string;
  description: string;
  /** Texto de la llamada a la acción ("Administrar", "Próximamente"...). */
  ctaLabel: string;
  /** Conteo opcional; si llega un número se pinta la píldora. */
  count?: number;
  /** Texto que sustituye al `0` en la píldora. Sin él, un cero se pinta como cero. */
  emptyLabel?: string;
  /** Acomoda la tarjeta en fila —icono, texto, conteo y CTA— en vez de en columna. */
  wide?: boolean;
  /** Atenúa la tarjeta y desactiva la navegación para secciones aún no disponibles. */
  disabled?: boolean;
  onClick?: () => void;
}

const Layout = styled(Card)<{ wide?: boolean }>(({ wide }) => ({
  display: 'flex',
  flexDirection: wide ? 'row' : 'column',
  alignItems: wide ? 'center' : 'stretch',
  gap: 16,
  // La tarjeta ancha se mide sola: forzar la altura de la tarjeta vertical
  // dejaría una franja hueca a lo largo de toda la pantalla.
  minHeight: wide ? 0 : 184,
}));

const Header = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});

// La CTA reparte etiqueta y flecha con `space-between`; a lo ancho del
// contenido quedarían pegadas, así que se le da un mínimo.
const CtaSlot = styled.div({
  minWidth: 108,
});

const Trailing = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 18,
  marginLeft: 'auto',
});

const Body = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
});

/**
 * Molécula central del hub. Ensambla los primitivos `Card`, `IconBadge`,
 * `CountBadge`, `Title`, `Description` y `Cta` en la tarjeta de una sección de un hub. No decide a dónde navega: solo emite `onClick`.
 */
const SectionCard = ({
  icon,
  iconBg,
  iconColor,
  title,
  description,
  ctaLabel,
  count,
  emptyLabel,
  wide = false,
  disabled = false,
  onClick,
}: SectionCardProps) => {
  const badge =
    typeof count === 'number' ? <CountBadge>{count === 0 && emptyLabel ? emptyLabel : count}</CountBadge> : null;

  const badgeIcon = (
    <IconBadge
      icon={icon}
      bg={disabled ? t.color.disabledIconBg : iconBg}
      color={disabled ? t.color.disabledIcon : iconColor}
    />
  );

  const texts = (
    <Body>
      <Title size="card" muted={disabled}>
        {title}
      </Title>
      <Description size="card" muted={disabled}>
        {description}
      </Description>
    </Body>
  );

  const cta = <Cta label={ctaLabel} color={disabled ? t.color.disabled : iconColor} />;

  return (
    <Layout wide={wide} interactive={!disabled} muted={disabled} onClick={disabled ? undefined : onClick}>
      {wide ? (
        <>
          {badgeIcon}
          {texts}
          <Trailing>
            {badge}
            <CtaSlot>{cta}</CtaSlot>
          </Trailing>
        </>
      ) : (
        <>
          <Header>
            {badgeIcon}
            {badge}
          </Header>
          {texts}
          {cta}
        </>
      )}
    </Layout>
  );
};

export default SectionCard;
